import React, { Fragment, useEffect, useRef, useState, } from 'react';
import { Button, Input } from 'reactstrap';
import { useZxing, DecodeHintType } from 'react-zxing';

import useQrScanner, { supportsBarcodeDetector } from './hooks/useQrScanner';

// @zxing/library の BarcodeFormat.QR_CODE
const QR_CODE = 11;

// 読み取り枠(中央)の割合。useQrScanner の ROI_RATIO と揃える
const ROI_RATIO = 0.7;

const hints = new Map([
  [DecodeHintType.POSSIBLE_FORMATS, [QR_CODE]],
  [DecodeHintType.TRY_HARDER, true],
]);

const constraints = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30 },
  },
};

export default function QrReader (props) {
  const { onRead } = props;
  const [engine, setEngine] = useState(null);
  const [result, setResult] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [showsManualInput, setShowsManualInput] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const hasReadRef = useRef(false);

  useEffect(() => {
    supportsBarcodeDetector().then(_ => setEngine(_ ? 'native' : 'zxing'));
  }, []);

  const onDecode = (text) => {
    if(hasReadRef.current) return;
    hasReadRef.current = true;
    setResult(text);
  };
  useEffect(() => {
    if(result === '') return;
    (async () => {
      await onRead(result);
      // 呼び出し側でモーダルが切り替わらなかった場合は再スキャンできるように戻す
      hasReadRef.current = false;
      setResult('');
      setManualCode('');
    })();
  }, [result]);

  const onSubmitManualCode = () => {
    const code = manualCode.trim();
    if(code === '') return;
    hasReadRef.current = true;
    setResult(code);
  };

  const viewProps = { paused: result !== '', onDecode, onError: setCameraError };

  return (
    <div style={{ width: '100%', maxWidth: 480 }}>
      <div className="position-relative bg-dark" style={{ borderRadius: 4, overflow: 'hidden' }}>
        {
          engine === 'native' ? (
            <NativeScannerView {...viewProps} />
          ) : engine === 'zxing' ? (
            <ZxingScannerView {...viewProps} />
          ) : (
            <div style={{ paddingTop: '75%' }} />
          )
        }
        <div
          className="position-absolute"
          style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', height: `${ROI_RATIO * 100}%`, aspectRatio: '1 / 1', maxWidth: `${ROI_RATIO * 100}%`, border: '2px solid rgba(255,255,255,0.8)', borderRadius: 8, pointerEvents: 'none' }}
        />
      </div>
      <div className="small text-muted mt-2">
        枠内にQRコードを写してください。ピントが合わない場合は少し離してからゆっくり近づけてください。
      </div>
      {
        cameraError != null && (
          <div className="alert alert-danger mt-2 mb-0 py-2 small">
            カメラを起動できませんでした。ブラウザのカメラ許可をご確認ください。
          </div>
        )
      }
      <div className="d-flex justify-content-end align-items-center mt-2">
        <Button size="sm" color="link" onClick={() => setShowsManualInput(!showsManualInput)}>
          読み取れない場合
        </Button>
      </div>
      {
        showsManualInput && (
          <div className="d-flex mt-2">
            <Input
              value={manualCode}
              onChange={_ => setManualCode(_.target.value)}
              onKeyDown={_ => _.key === 'Enter' && onSubmitManualCode()}
              placeholder="コードを直接入力"
            />
            <Button className="ml-2 text-nowrap" color="primary" onClick={onSubmitManualCode} disabled={manualCode.trim() === ''}>
              読込
            </Button>
          </div>
        )
      }
    </div>
  );
};

function TorchButton (props) {
  const { torch } = props;
  if(!torch.isAvailable) return null;

  return (
    <Button
      size="sm"
      color="light"
      className="position-absolute"
      style={{ right: 8, bottom: 8, opacity: 0.9 }}
      onClick={() => torch.isOn ? torch.off() : torch.on()}
    >
      <span className="fas fa-lightbulb mr-1" />
      ライト{torch.isOn ? 'オフ' : 'オン'}
    </Button>
  );
};

// BarcodeDetector 対応環境用。中央 ROI のみを OS のデコーダに渡す
function NativeScannerView (props) {
  const { paused, onDecode, onError } = props;
  const { videoRef, torch } = useQrScanner({ paused, onDecode, onError });

  return (
    <Fragment>
      <video ref={videoRef} style={{ width: '100%', display: 'block' }} muted playsInline />
      <TorchButton torch={torch} />
    </Fragment>
  );
};

// BarcodeDetector 非対応環境用のフォールバック
function ZxingScannerView (props) {
  const { paused, onDecode, onError } = props;
  const { ref, torch } = useZxing({
    constraints,
    hints,
    timeBetweenDecodingAttempts: 100,
    paused,
    onDecodeResult: _ => onDecode(_.getText()),
    onError,
  });

  return (
    <Fragment>
      <video ref={ref} style={{ width: '100%', display: 'block' }} muted playsInline />
      <TorchButton torch={torch} />
    </Fragment>
  );
};
